import {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useRef,
} from 'react';

import type { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { PrimaryContext } from '@/services';
import type { ShipmentSemen } from '@/services/shipping/types';
import { useContextMenu } from '@/utils/context-menu';

import { useSemenColumnDefs } from '../../utils/columnDefs';

type ShipmentSemenGridProps = {
  semen: ShipmentSemen[] | null | undefined;
  selectedSemen?: ShipmentSemen[];
  setSelectedSemen?: (semen: ShipmentSemen[]) => void;
  enableSelection?: boolean;
};

export type ShipmentSemenGridHandle = {
  deselectAll: () => void;
};

export const ShipmentSemenGrid = forwardRef<
  ShipmentSemenGridHandle,
  ShipmentSemenGridProps
>(({ semen, setSelectedSemen, enableSelection = false }, ref) => {
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const gridRef = useRef<AgGridReact<ShipmentSemen>>(null);

  useImperativeHandle(ref, () => ({
    deselectAll: () => {
      gridRef.current?.api?.deselectAll();
      setSelectedSemen?.([]);
    },
  }));

  const columnDefs = useSemenColumnDefs(enableSelection);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<ShipmentSemen>) => {
      setSelectedSemen?.(event.api.getSelectedRows());
    },
    [setSelectedSemen],
  );

  return (
    <div className="shipment__grid ag-grid-height-300">
      <WithLocalStorage storageKey="shipment-semen-grid">
        <AgGridReact
          gridOptions={defaultGridOptions}
          ref={gridRef}
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
          defaultColDef={defaultColDef}
          rowData={semen}
          columnDefs={columnDefs}
          getContextMenuItems={getContextMenuItems}
          rowSelection={enableSelection ? 'multiple' : undefined}
          onSelectionChanged={
            enableSelection ? handleSelectionChanged : undefined
          }
        />
      </WithLocalStorage>
    </div>
  );
});
